<template>
  <div
    class="relative inline-block navbar-dropdown-container"
    @mouseleave="handleMouseLeave"
  >
    <!-- Trigger Button -->
    <button
      @mouseenter="openDropdown"
      @click="toggleDropdown"
      class="font-bold text-base py-1 rounded transition-all duration-200 flex items-center space-x-1 focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2 navbar-dropdown-trigger"
      :class="[
        mobile ? 'block text-xl' : '',
      ]"
    >
      <span>{{ label }}</span>
      <svg
        class="w-4 h-4 transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        :style="{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <!-- Dropdown Overlay -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 transform -translate-y-2"
        enter-to-class="opacity-100 transform translate-y-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 transform translate-y-0"
        leave-to-class="opacity-0 transform -translate-y-2"
      >
        <div
          v-if="isOpen"
          class="fixed left-0 right-0 w-full primary-bg md:bg-white shadow-2xl border-t border-blue-100 z-[1200] dropdown-container"
          :style="{ top: dropdownTop + 'px' }"
          @mouseenter="handleDropdownMouseEnter"
          @mouseleave="handleDropdownMouseLeave"
        >
          <div class="max-w-screen-xl mx-auto px-8 md:py-8">
            <!-- Desktop Layout -->
            <div class="hidden md:grid grid-cols-3 gap-8">
              <!-- First Column - Main Categories -->
              <div class="space-y-4 md:col-span-1 md:h-[40vh] flex flex-col">
                <h3
                  v-if="firstColumnTitle"
                  class="text-lg font-bold text-blue-900 mb-6 border-b border-blue-100 pb-2 flex-shrink-0"
                >
                  {{ firstColumnTitle }}
                </h3>
                <div class="space-y-3 flex-1 overflow-y-auto pr-2">
                  <div
                    v-for="(category, index) in firstColumnItems"
                    :key="index"
                    @mouseenter="selectFirstLevel(index)"
                    @click="navigateToCategory(category)"
                    class="group cursor-pointer p-3 rounded-lg transition-all duration-200 hover:bg-blue-50 h-fit"
                    :class="{ 'bg-blue-50': selectedFirstLevel === index }"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center space-x-3">
                        <div>
                          <p
                            class="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors duration-200"
                          >
                            {{ category.name }}
                          </p>
                          <p
                            v-if="category.description"
                            class="text-sm text-gray-600 mt-1"
                          >
                            {{ category.description }}
                          </p>
                        </div>
                      </div>
                      <svg
                        class="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Second Column - Sub Categories (2 columns layout) -->
              <div
                class="space-y-4 md:col-span-2 md:h-[40vh] flex flex-col h-full"
              >
                <h3
                  v-if="secondColumnTitle"
                  class="text-lg font-bold text-primary mb-6 border-b border-blue-100 pb-2 flex-shrink-0"
                >
                  {{ secondColumnTitle }}
                </h3>
                <div
                  v-if="selectedFirstLevel !== null"
                  class="flex-1 overflow-y-auto"
                >
                  <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 transform translate-x-4"
                    enter-to-class="opacity-100 transform translate-x-0"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 transform translate-x-0"
                    leave-to-class="opacity-0 transform translate-x-4"
                  >
                    <div
                      class="grid grid-cols-2 gap-x-4 gap-y-2 overflow-y-auto pr-2"
                    >
                      <a
                        v-for="(subCategory, index) in secondColumnItems"
                        :key="index"
                        :href="subCategory.url"
                        class="group cursor-pointer p-3 rounded-lg transition-all duration-200 hover:bg-blue-50 h-fit"
                      >
                        <div class="flex items-center space-x-3">
                          <div
                            v-if="subCategory.icon"
                            class="w-6 h-6 flex items-center justify-center text-blue-600"
                          >
                            <component :is="subCategory.icon" class="w-4 h-4" />
                          </div>
                          <div class="flex-1">
                            <p
                              class="font-medium text-gray-900 group-hover:text-blue-700 transition-colors duration-200"
                            >
                              {{ subCategory.name }}
                            </p>
                            <p
                              v-if="subCategory.description"
                              class="text-sm text-gray-600 mt-1"
                            >
                              {{ subCategory.description }}
                            </p>
                            <div
                              v-if="subCategory.price"
                              class="text-sm font-semibold text-blue-600 mt-1"
                            >
                              {{ subCategory.price }}
                            </div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </Transition>
                </div>
                <div
                  v-else
                  class="text-gray-500 text-center py-8 flex-1 flex items-center justify-center"
                >
                  <div>
                    <svg
                      class="w-12 h-12 mx-auto text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    <p>Select a category to see options</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile Layout -->
            <div
              class="md:hidden overflow-hidden flex flex-col justify-stretch items-stretch h-[100dvh] primary-bg"
            >
              <!-- First Level - Main Categories -->
              <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 transform translate-x-4"
                enter-to-class="opacity-100 transform translate-x-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 transform translate-x-0"
                leave-to-class="opacity-0 transform -translate-x-4"
              >
                <div
                  v-if="currentLevel === 'first'"
                  class="flex flex-col h-full w-full absolute inset-0"
                >
                  <!-- Back button -->
                  <button
                    @click="closeDropdown"
                    class="flex items-center space-x-2 text-accent transition-colors duration-200 my-4 px-4 focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    <span class="font-medium">Back to Main Menu</span>
                  </button>

                  <!-- First column title -->
                  <h3
                    v-if="firstColumnTitle"
                    class="text-lg font-bold text-white mb-4 border-b border-blue-100 pb-2 flex-shrink-0"
                  >
                    {{ firstColumnTitle }}
                  </h3>
                  <div
                    class="min-h-0 flex-1 overflow-y-auto space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))]"
                  >
                    <div
                      v-for="(category, index) in firstColumnItems"
                      :key="index"
                      @click="selectFirstLevelMobile(index)"
                      class="group cursor-pointer p-4 rounded-lg transition-all duration-200 hover:bg-blue-50"
                    >
                      <div class="flex items-center justify-between text-white">
                        <div class="flex items-center space-x-3">
                          <div>
                            <p
                              class="font-semibold text-white group-hover:text-blue-700 transition-colors duration-200"
                            >
                              {{ category.name }}
                            </p>
                            <p
                              v-if="category.description"
                              class="text-sm text-white mt-1"
                            >
                              {{ category.description }}
                            </p>
                          </div>
                        </div>

                        <svg
                          class="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>

              <!-- Second Level - Sub Categories -->
              <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 transform translate-x-4"
                enter-to-class="opacity-100 transform translate-x-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 transform translate-x-0"
                leave-to-class="opacity-0 transform -translate-x-4"
              >
                <div
                  v-if="currentLevel === 'second'"
                  class="flex flex-col h-full w-full absolute inset-0"
                >
                  <h3
                    v-if="secondColumnTitle"
                    class="text-lg font-bold text-white mb-4 border-b border-blue-100 p-4 flex-shrink-0"
                  >
                    {{ secondColumnTitle }}
                  </h3>

                  <button
                    @click="goBackToFirstLevel"
                    class="flex items-center space-x-2 text-accent transition-colors duration-200 my-4 px-4 focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    <span class="font-medium"
                      >Back to {{ firstColumnTitle || "Categories" }}</span
                    >
                  </button>
                  <div
                    class="min-h-0 flex-1 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]"
                  >
                    <div class="grid grid-cols-1 gap-3">
                      <a
                        v-for="(subCategory, index) in secondColumnItems"
                        :key="index"
                        :href="subCategory.url"
                        class="group cursor-pointer p-4 rounded-lg transition-all duration-200 hover:bg-blue-50 text-white"
                      >
                        <div class="flex items-center space-x-3">
                          <div
                            v-if="subCategory.icon"
                            class="w-6 h-6 flex items-center justify-center text-white"
                          >
                            <component :is="subCategory.icon" class="w-4 h-4" />
                          </div>
                          <div class="flex-1">
                            <p
                              class="font-medium text-white group-hover:text-blue-700 transition-colors duration-200"
                            >
                              {{ subCategory.name }}
                            </p>
                            <p
                              v-if="subCategory.description"
                              class="text-sm text-white mt-1"
                            >
                              {{ subCategory.description }}
                            </p>
                            <div
                              v-if="subCategory.price"
                              class="text-sm font-semibold text-blue-600 mt-1"
                            >
                              {{ subCategory.price }}
                            </div>
                          </div>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";

interface MenuItem {
  name: string;
  description?: string;
  icon?: string;
  url?: string;
  price?: string;
  image?: string;
  children?: MenuItem[];
}

interface Props {
  label: string;
  isActive?: boolean;
  firstColumnTitle?: string;
  secondColumnTitle?: string;
  menuData: MenuItem[];
  featuredItems?: MenuItem[];
  mobile?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isActive: false,
  firstColumnTitle: undefined,
  secondColumnTitle: undefined,
  featuredItems: () => [],
  mobile: false,
});

const emit = defineEmits<{
  navigate: [item: MenuItem];
}>();

const isOpen = ref(false);
const selectedFirstLevel = ref<number | null>(null);
const scrollY = ref(0);
const currentLevel = ref<"first" | "second">("first");

// Computed properties for each column
const firstColumnItems = computed(() => props.menuData);

const secondColumnItems = computed(() => {
  if (selectedFirstLevel.value === null) return [];
  const selectedItem = props.menuData[selectedFirstLevel.value];
  return selectedItem?.children || [];
});

const secondColumnTitle = computed(() => {
  if (selectedFirstLevel.value === null) return "";
  const selectedItem = props.menuData[selectedFirstLevel.value];
  return selectedItem?.name || "";
});

const dropdownTop = computed(() => {
  // if viewport is less than 768px, return 0
  if (window.innerWidth < 768) {
    return 0;
  }

  // Get the navbar height and position
  const navbar = document.querySelector("header");
  if (navbar) {
    const rect = navbar.getBoundingClientRect();

    // Check if page is scrolled to top (or very close to top)
    const isAtTop = scrollY.value <= 10;

    if (isAtTop) {
      // When at top, use the header's offsetTop + offsetHeight to get the true bottom
      return navbar.offsetTop + navbar.offsetHeight;
    }

    // When scrolled, use the header bottom position from getBoundingClientRect
    return rect.bottom;
  }
  return 100; // fallback
});

// Methods
const openDropdown = () => {
  isOpen.value = true;
  // Set default selection to first item if no item is selected
  if (selectedFirstLevel.value === null && props.menuData.length > 0) {
    selectedFirstLevel.value = 0;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  selectedFirstLevel.value = null;
  currentLevel.value = "first";
};

let closeTimeout: number | null = null;

const handleMouseLeave = () => {
  // Set a small timeout to allow mouse to move to dropdown
  closeTimeout = window.setTimeout(() => {
    if (isOpen.value) {
      closeDropdown();
    }
  }, 100);
};

const handleDropdownMouseEnter = () => {
  // Clear the timeout and keep dropdown open
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = null;
  }
  isOpen.value = true;
};

const handleDropdownMouseLeave = () => {
  // Close dropdown when mouse leaves
  closeDropdown();
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) {
    selectedFirstLevel.value = null;
  } else {
    // Set default selection to first item when opening via click
    if (selectedFirstLevel.value === null && props.menuData.length > 0) {
      selectedFirstLevel.value = 0;
    }
  }
};

const selectFirstLevel = (index: number) => {
  selectedFirstLevel.value = index;
};

const selectFirstLevelMobile = (index: number) => {
  selectedFirstLevel.value = index;
  currentLevel.value = "second";
};

const goBackToFirstLevel = () => {
  currentLevel.value = "first";
  selectedFirstLevel.value = null;
};

const navigateToCategory = (category: MenuItem) => {
  if (category.url) {
    window.location.href = category.url;
  }
};

// Keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) return;

  switch (event.key) {
    case "Escape":
      closeDropdown();
      break;
    case "ArrowDown":
      event.preventDefault();
      if (selectedFirstLevel.value === null) {
        selectedFirstLevel.value = 0;
      } else {
        selectedFirstLevel.value = Math.min(
          props.menuData.length - 1,
          selectedFirstLevel.value + 1
        );
      }
      break;
    case "ArrowUp":
      event.preventDefault();
      if (selectedFirstLevel.value !== null) {
        selectedFirstLevel.value = Math.max(0, selectedFirstLevel.value - 1);
      }
      break;
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener("keydown", handleKeydown);

  // Add scroll event listener
  const handleScroll = () => {
    scrollY.value = window.scrollY;
  };
  window.addEventListener("scroll", handleScroll);

  // Store the cleanup function
  const cleanup = () => {
    window.removeEventListener("scroll", handleScroll);
  };

  // Store cleanup for onUnmounted
  onUnmounted(() => {
    cleanup();
  });
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  if (closeTimeout) {
    clearTimeout(closeTimeout);
  }
});
</script>

<style scoped>
/* Custom scrollbar for long lists */
.space-y-3::-webkit-scrollbar {
  width: 4px;
}

.space-y-3::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.space-y-3::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.space-y-3::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
